import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import React from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems } from "~/features/app/utils";
import { Icon } from "~/features/ui/icon";
import { useNavLayout } from "~/features/app/landing-header/context";
import { NavAdditionalLink } from "~/features/app/landing-header/nav-additional-links/nav-additional-link";
import Plus from "~/public/icons/plus.svg";
const fragment = graphql(`
  fragment LandingNavAdditionalDropdown on navAdditionalLinks_dropdown_BlockType {
    text: baseText
    links: children {
      ...LandingNavAdditionalLink
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  variant?: keyof typeof contentStyle;
};
const contentStyle = {
  transparent: {
    "backgroundColor": "rgba(0, 7, 4, 0.3) !important"
  },
  solid: {
    "--tw-bg-opacity": "1 !important",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity)) !important"
  },
  highlighted: {
    "--tw-bg-opacity": "1 !important",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity)) !important"
  }
} as const;
const borderStyle = {
  transparent: {
    "borderColor": "rgba(255, 255, 255, 0.2)"
  },
  solid: {
    "borderColor": "rgba(0, 7, 4, 0.2)"
  },
  highlighted: {
    "borderColor": "rgba(0, 7, 4, 0.2)"
  }
} as const;
const hoverStyle = {
  transparent: {
    "@media (hover: hover)": {
      "&:hover": {
        "backgroundColor": "rgba(0, 7, 4, 0.3)"
      },
      "&:hover::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }
  },
  solid: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
      },
      "&:hover::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }
  },
  highlighted: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
      },
      "&:hover::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }
  }
};
export function NavAdditionalDropdown(props: Props) {
  const {
    variant,
    ...rest
  } = props;
  const {
    text,
    links
  } = useFragment(fragment, rest);
  const [isOpen, setIsOpen] = React.useState(false);
  const {
    navTheme
  } = useNavLayout();
  const surface = variant || navTheme;
  if (!hasItems(links)) {
    return null;
  }
  return <RadixDropdownMenu.Root onOpenChange={open => setIsOpen(open)}>
      <RadixDropdownMenu.Trigger asChild>
        <_StyledButton aria-label={text} $_css={[{
        "position": "relative",
        "display": "flex",
        "height": "100%",
        "alignItems": "center",
        "paddingLeft": "5.2rem",
        "paddingRight": "5.2rem",
        "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
        "transitionDuration": "250ms",
        "transitionTimingFunction": "ease-in-out"
      }, {
        "&::before": {
          "content": "var(--tw-content)",
          "position": "absolute",
          "left": "0",
          "right": "0",
          "top": "0",
          "height": "0.4rem",
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
          "opacity": "0",
          "transitionProperty": "opacity",
          "transitionDuration": "250ms",
          "transitionTimingFunction": "ease-in-out",
          "--tw-content": "\"\""
        }
      }, isOpen && {
        "--tw-backdrop-blur": "blur(8px)",
        "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
        "&::before": {
          "content": "var(--tw-content)",
          "opacity": "1"
        }
      }, isOpen && contentStyle[surface], hoverStyle[surface]]}>
          <_StyledDiv>
            <_StyledIcon icon={Plus} size="12" $_css2={[{
            "position": "absolute",
            "left": "0",
            "top": "0",
            "--tw-translate-x": "-100%",
            "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
            "--tw-translate-y": "-50%",
            "transitionProperty": "transform",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out"
          }, isOpen ? {
            "--tw-rotate": "45deg",
            "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
          } : undefined]} />
            <span>{text}</span>
          </_StyledDiv>
        </_StyledButton>
      </RadixDropdownMenu.Trigger>
      <_StyledRadixDropdownMenuContent align="start" sideOffset={1} $_css3={[{
      "display": "flex",
      "width": "var(--radix-popper-anchor-width)",
      "flexDirection": "column",
      "--tw-backdrop-blur": "blur(8px)",
      "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
    }, contentStyle[surface], {
      "@keyframes radix-dropdown-menu-collapse-keyframes": {
        "from": {
          "transform": "scaleY(1)"
        },
        "to": {
          "transform": "scaleY(0)"
        }
      },
      "&[data-state=\"closed\"]": {
        "animation": "radix-dropdown-menu-collapse-keyframes 250ms ease-in-out 250ms"
      },
      "@keyframes radix-dropdown-menu-expand-keyframes": {
        "from": {
          "transform": "scaleY(0)"
        },
        "to": {
          "transform": "scaleY(1)"
        }
      },
      "&[data-state=\"open\"]": {
        "animation": "radix-dropdown-menu-expand-keyframes 250ms ease-in-out"
      }
    }, {
      "transformOrigin": "var(--radix-dropdown-menu-content-transform-origin)"
    }]}>
        {links.map((link, idx) => <_StyledRadixDropdownMenuItem style={{
        transitionDelay: isOpen ? `${idx * 80 + 250}ms` : "0ms"
      }} key={idx} $_css4={[{
        "opacity": "0",
        "transitionProperty": "opacity",
        "transitionDuration": "250ms",
        "transitionTimingFunction": "ease-in-out",
        "&:not(:last-child)": {
          "borderBottomWidth": "1px"
        }
      }, borderStyle[surface], isOpen && {
        "opacity": "1"
      }]}>
            <_StyledNavAdditionalLink {...link} />
          </_StyledRadixDropdownMenuItem>)}
      </_StyledRadixDropdownMenuContent>
    </RadixDropdownMenu.Root>;
}
var _StyledButton = /*#__PURE__*/_styled("button").withConfig({
  componentId: "sc-sf-157zd25-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-157zd25-1"
})({
  "position": "relative"
});
var _StyledIcon = _styled3(Icon).withConfig({
  componentId: "sc-sf-157zd25-2"
})`${p => p.$_css2}`;
var _StyledRadixDropdownMenuContent = _styled4(RadixDropdownMenu.Content).withConfig({
  componentId: "sc-sf-157zd25-3"
})`${p => p.$_css3}`;
var _StyledRadixDropdownMenuItem = _styled5(RadixDropdownMenu.Item).withConfig({
  componentId: "sc-sf-157zd25-4"
})`${p => p.$_css4}`;
var _StyledNavAdditionalLink = /*#__PURE__*/_styled6(NavAdditionalLink).withConfig({
  displayName: "nav-additional-dropdown___StyledNavAdditionalLink",
  componentId: "sc-1y0ykco-0"
})({
  "height": "5.6rem",
  "width": "100%",
  "whiteSpace": "nowrap",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem"
});